// src/pages/RiwayatGeneratePage.jsx
import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { supabase } from "../supabaseClient.js";
import { MEMORY_CACHE, BLOB_CACHE, InputCache } from '../services/GlobalCache.js';
import CacheService from '../services/CacheService.js';

// DashboardNavbar removed
import { LayoutGrid, List, Play, Music, Download, X, Loader2, AlertCircle, FileText, ChevronDown, Check, RotateCcw, Edit3, Trash2 } from "lucide-react";
import ImageEditorModal from "../components/ImageEditorModal";

// --- HELPERS FROM GenericAiPage ---

// Local InputCache removed - Imported from GlobalCache

// --- DELETE ITEM MODAL (Copied Logic) ---
const DeleteItemModal = ({ isOpen, onClose, onConfirm, title, message }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm animate-in fade-in duration-200" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 animate-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()}>
        <div className="flex flex-col text-left">
          <h3 className="text-lg font-bold text-slate-800 mb-2 px-4">{title}</h3>
          <p className="text-sm text-slate-600 mb-6 leading-relaxed px-4">{message}</p>
          <div className="flex gap-3 w-full px-4 justify-end">
            <button
              onClick={onClose}
              className="py-2.5 px-4 text-slate-500 font-medium hover:text-slate-700 transition-colors text-xs sm:text-sm"
            >
              Batal
            </button>
            <button
              onClick={onConfirm}
              className="py-2.5 px-6 rounded-xl bg-white border border-red-200 text-red-600 font-bold hover:bg-red-50 hover:border-red-300 hover:shadow-sm transition-all text-xs sm:text-sm"
            >
              Hapus
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// 1. Clean Filename

// 1. Clean Filename
const getCleanFilename = (url) => {
  if (!url) return "Document";
  try { url = decodeURIComponent(url); } catch (e) { }

  let filenamePart = url.split("/").pop() || "";

  // Check for NEW format: Name_TIMESTAMP.ext (e.g., headline_173434.. .txt)
  const match = filenamePart.match(/^(.*)_(\d{13})\.([a-zA-Z0-9]+)$/);

  if (match) {
    const originalName = match[1];
    const timestamp = parseInt(match[2]);
    const ext = match[3];

    // Format Date: DD-MM-YYYY_HHmm
    const date = new Date(timestamp);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hour = String(date.getHours()).padStart(2, '0');
    const minute = String(date.getMinutes()).padStart(2, '0');

    return `${originalName}_${day}-${month}-${year}_${hour}${minute}.${ext}`;
  }

  // Fallback
  if (filenamePart.includes(';')) {
    filenamePart = filenamePart.split(';')[0];
  }
  return filenamePart;
};

// --- Unified Preview Component (Synced with GenericAiPage) ---
const UnifiedPreview = ({ result, mimeType, isHistory }) => {
  const [textContent, setTextContent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [unwrappedMedia, setUnwrappedMedia] = useState(null); // { url, type }

  useEffect(() => {
    // Helper to unwrap N8N JSON output
    const checkAndUnwrap = async (url) => {
      try {
        const r = await fetch(url);
        const text = await r.text();
        setTextContent(text); // Store raw text anyway

        // Try JSON
        try {
          const json = JSON.parse(text);
          // Common N8N keys
          const mediaUrl = json.output || json.url || json.video || json.image || json.audio || (Array.isArray(json) && json[0]);
          if (mediaUrl && typeof mediaUrl === 'string' && mediaUrl.startsWith('http')) {
            // Infer type from new URL
            const ext = mediaUrl.split(/[#?]/)[0].split('.').pop().trim().toLowerCase();
            let type = null;
            if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) type = 'image';
            else if (['mp4', 'webm', 'mov', 'mkv'].includes(ext)) type = 'video';
            else if (['mp3', 'wav', 'ogg', 'm4a', 'flac', 'aac'].includes(ext)) type = 'audio';

            if (type) {
              setUnwrappedMedia({ url: mediaUrl, type });
              return;
            }
          }
        } catch (e) { /* not json */ }
      } catch (e) { console.error("Unwrap failed", e); }
      setIsLoading(false);
    };

    const isJson = mimeType === "application/json" || result.public_url?.endsWith(".json");
    const isText = mimeType === "text/plain" || result.public_url?.endsWith(".txt");

    if ((isJson || isText) && result.public_url) {
      checkAndUnwrap(result.public_url);
    } else {
      setIsLoading(false);
    }
  }, [result.public_url, mimeType]);

  // USE UNWRAPPED MEDIA IF AVAILABLE
  const effectiveUrl = unwrappedMedia ? unwrappedMedia.url : result.public_url;
  const effectiveType = unwrappedMedia ? unwrappedMedia.type : null;

  // Refined Mime Check (Priority: Unwrapped Type -> Mime String -> Extension Inference)
  const isVideo = effectiveType === 'video' || mimeType?.startsWith('video');
  const isImage = effectiveType === 'image' || mimeType?.startsWith('image');
  const isAudio = effectiveType === 'audio' || mimeType?.startsWith('audio');
  const isPdf = mimeType === 'application/pdf' || effectiveUrl?.endsWith('.pdf');

  // 1. VIDEO
  if (isVideo) {
    if (isHistory) {
      // Grid: Thumbnail with play icon
      return (
        <div className="w-full h-full bg-black flex items-center justify-center overflow-hidden relative group">
          <video
            src={`${effectiveUrl}#t=0.1`}
            preload="metadata"
            className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity"
            muted
            playsInline
            disablePictureInPicture
          />
          <div className="absolute inset-0 flex items-center justify-center bg-black/10 group-hover:bg-black/30 transition-all">
            <div className="w-8 h-8 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center border border-white/30 text-white shadow-lg group-hover:scale-110 transition-transform">
              <Play size={12} fill="currentColor" className="ml-0.5" />
            </div>
          </div>
        </div>
      );
    }
    // Modal: Full Player
    return (
      <div className="w-full h-full bg-black flex items-center justify-center overflow-hidden">
        <video controls autoPlay src={effectiveUrl} className="w-full h-full object-contain" />
      </div>
    );
  }

  // 2. IMAGE
  if (isImage) {
    // Use BLOB_CACHE for 0ms loading, fallback to standard URL
    const displayUrl = BLOB_CACHE.get(effectiveUrl) || effectiveUrl;
    return (
      <div className="w-full h-full flex items-center justify-center overflow-hidden bg-black">
        <img
          src={displayUrl}
          alt="Result"
          className={`w-full h-full ${isHistory ? "object-cover group-hover:scale-105 transition-transform duration-500" : "object-contain"}`}
          loading="eager"
        />
      </div>
    );
  }

  // 3. AUDIO
  if (isAudio) {
    if (isHistory) {
      return (
        <div className="w-full h-full bg-gradient-to-br from-indigo-50 to-purple-50 flex flex-col items-center justify-center p-2 text-indigo-400">
          <Music size={24} className="mb-2 opacity-50" />
          <span className="text-[9px] font-bold uppercase tracking-wider opacity-60">Audio</span>
        </div>
      );
    }
    return (
      <div className="w-full h-full bg-black/90 flex items-center justify-center p-10">
        <audio controls autoPlay src={effectiveUrl} className="w-full max-w-2xl" />
      </div>
    );
  }

  // 4. DOCUMENTS & TEXT
  // Grid View for Docs: Icon + Name
  if (isHistory) {
    const name = getCleanFilename(effectiveUrl);
    return (
      <div className="w-full h-full bg-slate-50 p-2 overflow-hidden flex flex-col items-center justify-center text-center gap-2 border-inherit">
        <div className="w-10 h-10 rounded-lg bg-sky-100 text-sky-600 flex items-center justify-center">
          <FileText size={20} />
        </div>
        <div className="w-full px-1">
          <p className="text-[10px] font-bold text-slate-700 truncate w-full" title={name}>
            {name}
          </p>
          <p className="text-[9px] text-slate-400 uppercase tracking-wider mt-0.5">
            {isPdf ? 'PDF' : 'DOC'}
          </p>
        </div>
      </div>
    );
  }

  // Modal View for Docs
  if (isPdf) {
    return <iframe src={effectiveUrl} className="w-full h-full" title="PDF Preview" />;
  }
  if (mimeType === "text/plain" || mimeType === "application/json" || effectiveUrl?.endsWith(".json")) {
    return (
      <div className="w-full h-full bg-white p-8 overflow-y-auto whitespace-pre-wrap font-mono text-xs text-slate-700 mx-auto">
        <div className="max-w-4xl mx-auto">{textContent || "Loading content..."}</div>
      </div>
    );
  }
  // Google Viewer Fallback
  return (
    <iframe
      src={`https://docs.google.com/gview?url=${encodeURIComponent(effectiveUrl)}&embedded=true`}
      className="w-full h-full"
      title="Doc Preview"
    />
  );
};


// --- PERSISTENT CACHE HELPER (Replaced by CacheService) ---
// CacheService is imported and used directly.


// --- MAIN PAGE COMPONENT ---
const RiwayatGeneratePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setPageTitle, profile: ctxProfile } = useOutletContext();

  useEffect(() => {
    setPageTitle(t('history.pageTitle'));
  }, [setPageTitle, t]);

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(() => ctxProfile || CacheService.get('user_profile') || null);
  const [userFeatures, setUserFeatures] = useState([]);
  const [sidebarItemsDb, setSidebarItemsDb] = useState([]);

  // Runs State
  const [runs, setRuns] = useState([]);
  const [runsLoading, setRunsLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  // Modal State
  const [mediaModal, setMediaModal] = useState(null); // { public_url, mime_type, ... }

  // Action States
  const [assetToDelete, setAssetToDelete] = useState(null);
  const [isDeletingAsset, setIsDeletingAsset] = useState(false);

  const [imageEditorAsset, setImageEditorAsset] = useState(null);
  const [isSavingEditedImage, setIsSavingEditedImage] = useState(false);

  // Helper: Show Toast (Simple alert wrapper for now, or use context if available)
  const showToast = (message, type = 'info') => {
    // Assuming a toast system exists or fallback to alert for critical errors
    // For now we'll just log or alert on error
    if (type === 'error') alert(message);
    else console.log(message);
  };

  // Tabs & Views
  const [historyTab, setHistoryTab] = useState("generate");
  const [viewMode, setViewMode] = useState("list");
  const [gridSize, setGridSize] = useState("small");
  const [isViewMenuOpen, setIsViewMenuOpen] = useState(false);

  const GRID_SIZES = {
    mini: "grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10",
    small: "grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8",
    medium: "grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5",
    large: "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3",
  };

  // Pagination / Scroll
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const observerTarget = useRef(null);

  // Transactions
  const [transactions, setTransactions] = useState([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [currentTxPage, setCurrentTxPage] = useState(1);
  const [totalTxPages, setTotalTxPages] = useState(1);

  // Fetch Transactions (Paginated)
  const fetchTransactions = async (userId, page = 1, silent = false) => {
    if (!silent) setTransactionsLoading(true);
    try {
      const from = (page - 1) * itemsPerPage;
      const to = from + itemsPerPage - 1;

      const { data, count, error } = await supabase
        .from("transactions")
        .select("*", { count: 'exact' })
        .eq("user_id", userId)
        .eq("status", "paid")
        .order("paid_at", { ascending: false })
        .range(from, to);

      if (error) throw error;
      const newData = data || [];
      setTransactions(newData);

      // Save to Cache (Page 1)
      if (page === 1) {
        CacheService.set(`history_tx_${userId}`, newData, 43200); // 30 Days
      }


      if (count) setTotalTxPages(Math.ceil(count / itemsPerPage));
    } catch (err) {
      console.error("Tx Fetch Error", err);
    } finally {
      if (!silent) setTransactionsLoading(false);
    }
  };

  // Trigger Fetch on Tx Page Change
  useEffect(() => {
    if (user && historyTab === 'topup') {
      fetchTransactions(user.id, currentTxPage);
    }
  }, [currentTxPage, user, historyTab, itemsPerPage]);

  // Request Tracking
  const latestRequestId = useRef(0);

  // Fetch Runs (Safe Mode) - Added Auto-Retry
  const fetchRuns = async (userId, page, append = false, silent = false, retryCount = 0) => {
    if (!userId) return;

    // Request ID for cancellation check
    const requestId = ++latestRequestId.current;

    if (!silent) setRunsLoading(true);
    if (retryCount === 0) setFetchError(null); // Only clear error on first attempt

    try {
      const from = (page - 1) * itemsPerPage;
      const to = from + itemsPerPage - 1;

      // START QUERY
      let query = supabase
        .from("ai_model_runs")
        .select("id, created_at, feature_code, model_name, model_key, credits_charged, credit_before, credit_after, result, inputs", { count: 'exact' })
        .eq("user_id", userId);

      // CONDITIONAL LOGIC:
      if (viewMode === 'grid') {
        query = query
          .not('result', 'cs', '{"is_cleaned": true}')   // 1. Exclude expired/deleted files
          .not('result->>public_url', 'is', null);       // 2. Exclude text-only (no public_url)
      }

      const { data, error, count } = await query
        .order("created_at", { ascending: false })
        .range(from, to);

      // STALE CHECK: If a new request started while we were waiting, ignore this result
      if (requestId !== latestRequestId.current) return;

      if (error) throw error;

      const newData = data || [];

      // Process result structure
      const processedData = newData.flatMap(run => {
        let res = run.result || {};
        // Handle Array Results (Multipart/Zip)
        if (Array.isArray(res)) {
          return res.map((fileItem, idx) => ({
            ...run,
            id: `${run.id}_part_${idx}`,
            original_id: run.id,
            result: {
              ...fileItem,
              public_url: fileItem.public_url || fileItem.url || fileItem.output,
              mime_type: fileItem.mime_type
            },
            is_multipart: true,
            part_index: idx + 1,
            total_parts: res.length
          }));
        }
        // Handle Legacy Single Result
        let publicUrl = res.public_url || res.url || res.output || res.video || res.image || res.audio;
        if (Array.isArray(publicUrl)) publicUrl = publicUrl[0];

        let mimeType = res.mime_type;
        if (!mimeType && publicUrl && typeof publicUrl === 'string') {
          const ext = publicUrl.split(/[#?]/)[0].split('.').pop().trim().toLowerCase();
          if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) mimeType = 'image/jpeg';
          else if (['mp4', 'webm', 'mov', 'mkv'].includes(ext)) mimeType = 'video/mp4';
          else if (['mp3', 'wav', 'ogg', 'm4a', 'flac'].includes(ext)) mimeType = 'audio/mpeg';
          else if (['pdf'].includes(ext)) mimeType = 'application/pdf';
          else if (['txt'].includes(ext)) mimeType = 'text/plain';
          else if (['json'].includes(ext)) mimeType = 'application/json';
        }
        return [{ ...run, result: { ...res, public_url: publicUrl, mime_type: mimeType } }];
      });

      if (append) {
        setRuns(prev => [...prev, ...processedData]);
      } else {
        setRuns(processedData);
        // Save to Cache (Page 1)
        if (page === 1) {
          CacheService.set(`history_runs_v2_${userId}`, processedData, 43200); // 30 Days
          MEMORY_CACHE[`riwayat_runs_v2_${userId}`] = processedData;
        }
      }

      // PREFETCH IMAGES TO BLOB_CACHE (True 0ms)
      const imagesToPrefetch = processedData.slice(0, 30).filter(run => {
        const url = run.result?.public_url;
        if (!url) return false;
        const mime = run.result?.mime_type || '';
        const ext = url.split(/[#?]/)[0].split('.').pop().trim().toLowerCase();
        return mime.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);
      });

      // Background prefetch - don't block UI
      imagesToPrefetch.forEach(run => {
        const url = run.result.public_url;
        if (!BLOB_CACHE.get(url)) {
          BLOB_CACHE.prefetch(url).catch(e => console.warn('Blob prefetch warning:', e));
        }
      });

      if (count !== null) {
        setTotalPages(Math.ceil(count / itemsPerPage));
        if (from + newData.length >= count || newData.length === 0) setHasMore(false);
        else setHasMore(true);
      } else {
        if (newData.length < itemsPerPage) setHasMore(false);
      }

    } catch (err) {
      if (requestId !== latestRequestId.current) return; // Ignore stale errors

      console.error("Fetch Runs Error", err);

      // AUTO RETRY LOGIC (1 Attempt)
      if (retryCount < 1) {
        console.log("Retrying fetch...");
        setTimeout(() => {
          if (requestId === latestRequestId.current) { // Only retry if still valid
            fetchRuns(userId, page, append, silent, retryCount + 1);
          }
        }, 500); // Wait 500ms
        return; // Don't show error yet
      }

      // STOP LOOP
      setHasMore(false);
      setFetchError("Gagal mengambil data. Kuota database mungkin penuh atau koneksi terputus.");
    } finally {
      if (requestId === latestRequestId.current) {
        if (!silent) setRunsLoading(false);
      }
    }
  };

  // Observer for Infinite Scroll
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && !runsLoading && hasMore && viewMode === 'grid') {
        setCurrentPage(p => p + 1);
      }
    }, { threshold: 0.5 });

    if (observerTarget.current) observer.observe(observerTarget.current);
    return () => observer.disconnect();
  }, [runsLoading, hasMore, viewMode]);

  // Trigger Fetch on Page Change (AND Initial Load since removed from init)
  useEffect(() => {
    // Smart Silent Mode: If we have cached data on page 1, don't show spinner
    const isFirstPage = currentPage === 1;
    const hasData = runs.length > 0;
    const shouldBeSilent = isFirstPage && hasData;

    if (user && currentPage > 1 && viewMode === 'grid') {
      fetchRuns(user.id, currentPage, true);
    } else if (user && viewMode === 'list') {
      // For List View, always fetch (replace) when page changes
      fetchRuns(user.id, currentPage, false, shouldBeSilent);
    } else if (user && viewMode === 'grid' && currentPage === 1) {
      // Grid view initial load (or switch to grid)
      fetchRuns(user.id, 1, false, shouldBeSilent);
    }
  }, [currentPage, user, viewMode, itemsPerPage]);

  // Initial Data Load
  useEffect(() => {
    const init = async () => {
      try {




        setLoading(true);

        const { data: { user: authUser }, error: authError } = await supabase.auth.getUser();
        if (authError || !authUser) { navigate('/login'); return; }
        setUser(authUser);

        // Fetch profile (required)
        try {
          const { data: profileData } = await supabase
            .from("profiles").select("*").eq("id", authUser.id).single();
          if (profileData) setProfile(profileData);
        } catch (e) { console.warn('profiles fetch failed'); }

        // Fetch user features (optional)
        try {
          const { data: featuresData } = await supabase
            .from("user_features").select("feature_code").eq("user_id", authUser.id);
          if (featuresData) setUserFeatures(featuresData.map(f => f.feature_code));
        } catch (e) { console.warn('user_features not available'); }

        // --- Cache Check: MEMORY_CACHE (0ms) -> localStorage ---
        let runsCache = MEMORY_CACHE[`riwayat_runs_v2_${authUser.id}`];
        if (!runsCache) {
          runsCache = CacheService.get(`history_runs_v2_${authUser.id}`);
          if (runsCache) {
            MEMORY_CACHE[`riwayat_runs_v2_${authUser.id}`] = runsCache; // Hydrate RAM
          }
        }

        const txCache = CacheService.get(`history_tx_${authUser.id}`);

        if (runsCache) {
          setRuns(runsCache);
          setRunsLoading(false);

          // PREFETCH CACHED IMAGES TO BLOB_CACHE (True 0ms on next render)
          const imagesToPrefetch = runsCache.slice(0, 30).filter(run => {
            const url = run.result?.public_url;
            if (!url) return false;
            const mime = run.result?.mime_type || '';
            const ext = url.split(/[#?]/)[0].split('.').pop().trim().toLowerCase();
            return mime.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);
          });

          imagesToPrefetch.forEach(run => {
            const url = run.result.public_url;
            if (!BLOB_CACHE.get(url)) {
              BLOB_CACHE.prefetch(url).catch(() => { });
            }
          });
        }
        if (txCache) {
          setTransactions(txCache);
          setTransactionsLoading(false);
        }

      } catch (e) {
        console.error("Init Error", e);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [navigate]);



  // --- ACTIONS HANDLERS ---

  // 1. RECREATE
  const handleRecreate = async (e, run) => {
    e.stopPropagation();
    if (!run.feature_code) {
      alert(t('history.featureNotAvailable') || "Feature not available");
      return;
    }

    // Save inputs to cache logic (RAM Store)
    let savedInputs = run.inputs || {};
    if (typeof savedInputs === 'string') {
      try { savedInputs = JSON.parse(savedInputs); } catch (e) { savedInputs = {}; }
    }

    if (user?.id) {
      InputCache.save(run.feature_code, user.id, savedInputs);
    }

    // Delegate navigation to Sidebar to ensure correct Menu Expansion and Model Data
    const event = new CustomEvent('neo-request-recreate', {
      detail: {
        featureSlug: run.feature_code,
        modelKey: run.model_key,
        modelName: run.model_name
      }
    });
    window.dispatchEvent(event);
  };

  // 2. DELETE (Smart Delete)
  const handleDeleteItem = (e, run) => {
    e.stopPropagation();
    setAssetToDelete(run);
  };

  const confirmDeleteAsset = async () => {
    if (!assetToDelete || !user?.id) return;
    setIsDeletingAsset(true);
    try {
      // Logic: Update ai_model_runs result to null/splice (Soft Delete)
      // Since history items are results, and we want to "delete the image" but keep the record?
      // The user said "Deleting image logic is same", implying we remove the content from the row.

      let currentResult = assetToDelete.result;
      let newResult = null;

      if (assetToDelete.is_multipart && assetToDelete.original_id) {
        // It's part of an array in DB
        // We need to fetch the ORIGINAL row first to get the full array
        const { data: rowData, error: fetchError } = await supabase
          .from('ai_model_runs')
          .select('result')
          .eq('id', assetToDelete.original_id)
          .single();

        if (fetchError || !rowData) throw new Error("Item not found");

        let dbResult = rowData.result;
        if (typeof dbResult === 'string') {
          try { dbResult = JSON.parse(dbResult); } catch (e) { }
        }

        if (Array.isArray(dbResult)) {
          // Remove index. Note: part_index is 1-based index (idx + 1)
          const idx = assetToDelete.part_index - 1;
          if (idx >= 0 && idx < dbResult.length) {
            dbResult.splice(idx, 1);
            newResult = dbResult.length === 0 ? null : dbResult;
          } else {
            newResult = dbResult; // No change
          }
        } else {
          newResult = null;
        }
      } else {
        // Single item
        newResult = null;
      }

      // Update DB
      const idToUpdate = assetToDelete.original_id || assetToDelete.id;
      const { error: updateError } = await supabase
        .from('ai_model_runs')
        .update({ result: newResult })
        .eq('id', idToUpdate);

      if (updateError) throw updateError;

      // Update Local State
      showToast("Item berhasil dihapus.", "success");
      setRuns(prev => prev.filter(r => r.id !== assetToDelete.id)); // Remove from UI
      setAssetToDelete(null);

    } catch (err) {
      console.error("Delete failed", err);
      showToast("Gagal menghapus item.", "error");
    } finally {
      setIsDeletingAsset(false);
    }
  };

  // 3. EDIT
  const handleEdit = (e, run) => {
    e.stopPropagation();
    // Only for images
    if (run.result?.public_url) {
      setImageEditorAsset({ ...run, url: run.result.public_url });
    }
  };

  const handleSaveEditedImage = async (blob) => {
    if (!imageEditorAsset || !user) return;
    setIsSavingEditedImage(true);
    try {
      const timestamp = Date.now();
      const filename = `edited_${timestamp}.png`;
      const filePath = `${user.id}/${filename}`;

      const { data: uploadData, error: uploadError } = await supabase.storage
        .from("asset-user")
        .upload(filePath, blob, {
          contentType: "image/png",
          upsert: false
        });

      if (uploadError) throw uploadError;

      const { data: publicData } = supabase.storage.from("asset-user").getPublicUrl(filePath);
      const publicUrl = publicData.publicUrl;

      showToast("Gambar berhasil disimpan ke Recent Assets.", "success");

      // Optional: We could offer to recreate immediately, but for now just save.
      setImageEditorAsset(null);

    } catch (err) {
      console.error("Save edit failed", err);
      showToast("Gagal menyimpan gambar.", "error");
    } finally {
      setIsSavingEditedImage(false);
    }
  };


  // --- Download Handler ---
  const handleDownload = async (url, filename) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Download failed:", error);
      alert(t('history.downloadFailed'));
    }
  };

  if (loading) {
    return (
      <div className="h-screen w-full bg-slate-50 p-6 flex flex-col space-y-6 animate-pulse">
        {/* Header */}
        <div className="w-full h-14 bg-slate-200 rounded-xl"></div>
        {/* Table Rows */}
        <div className="space-y-3">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(i => (
            <div key={i} className="w-full h-12 bg-slate-200 rounded-lg"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <>
      <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8 h-full">
        <div className="max-w-6xl mx-auto space-y-4">

          {/* Header Controls */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-2">
              {['generate', 'topup'].map(tab => (
                <button
                  key={tab}
                  onClick={() => setHistoryTab(tab)}
                  className={`px-4 py-2 rounded-full text-xs font-semibold border transition-all capitalize ${historyTab === tab
                    ? "bg-sky-600 border-sky-600 text-white shadow-md"
                    : "bg-white border-slate-200 text-slate-500 hover:bg-slate-50"
                    }`}
                >
                  {tab === 'topup' ? t('history.tabTransaction') : t('history.tabGenerate')}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2 ml-auto">
              {/* Page Size Selector - Next to View Options */}
              {viewMode === 'list' && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500">{t('history.rowsPerPage')}</span>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      if (historyTab === 'generate') {
                        setCurrentPage(1);
                      } else {
                        setCurrentTxPage(1);
                      }
                    }}
                    className="bg-white border border-slate-200 rounded-md px-3 py-1.5 text-xs font-medium text-slate-600 hover:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent cursor-pointer transition-all"
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
              )}

              {historyTab === 'generate' && (
                <div className="relative">
                  <button
                    onClick={() => setIsViewMenuOpen(!isViewMenuOpen)}
                    className="flex items-center gap-2 bg-white px-3 py-2 rounded-lg border border-slate-200 text-xs font-semibold text-slate-700 hover:bg-slate-50 transition-all shadow-sm"
                  >
                    {viewMode === 'list' ? <List size={14} /> : <LayoutGrid size={14} />}
                    <span>{t('history.viewOptions')}</span>
                    <ChevronDown size={14} className={`text-slate-400 transition-transform ${isViewMenuOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {/* DROPDOWN MENU */}
                  {isViewMenuOpen && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setIsViewMenuOpen(false)} />
                      <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-xl border border-slate-200 shadow-xl p-1 z-20 animate-in fade-in zoom-in-95 duration-200">

                        <div className="px-3 py-2 text-[10px] font-bold text-slate-400 uppercase tracking-wider">{t('history.layout')}</div>
                        <button onClick={() => {
                          if (viewMode !== 'list') { setViewMode('list'); setRuns([]); fetchRuns(user.id, 1, false); setCurrentPage(1); }
                          setIsViewMenuOpen(false);
                        }} className="w-full flex items-center justify-between px-3 py-2 rounded-lg hover:bg-slate-50 text-xs text-slate-700 font-medium">
                          <div className="flex items-center gap-2"><List size={14} /> {t('history.list')}</div>
                          {viewMode === 'list' && <Check size={14} className="text-sky-600" />}
                        </button>
                        <button onClick={() => {
                          if (viewMode !== 'grid') { setViewMode('grid'); setRuns([]); fetchRuns(user.id, 1, false); setCurrentPage(1); }
                          setIsViewMenuOpen(false);
                        }} className="w-full flex items-center justify-between px-3 py-2 rounded-lg hover:bg-slate-50 text-xs text-slate-700 font-medium">
                          <div className="flex items-center gap-2"><LayoutGrid size={14} /> {t('history.grid')}</div>
                          {viewMode === 'grid' && <Check size={14} className="text-sky-600" />}
                        </button>

                        <div className="border-t border-slate-100 my-1" />

                        <div className="px-3 py-2 text-[10px] font-bold text-slate-400 uppercase tracking-wider">{t('history.imageSize')}</div>
                        {['mini', 'small', 'medium', 'large'].map(size => (
                          <button
                            key={size}
                            onClick={() => { setGridSize(size); setViewMode('grid'); setIsViewMenuOpen(false); }}
                            className={`w-full flex items-center justify-between px-3 py-2 rounded-lg hover:bg-slate-50 text-xs font-medium capitalize ${gridSize === size && viewMode === 'grid' ? 'text-sky-700 bg-sky-50' : 'text-slate-700'}`}
                          >
                            <span className="pl-6">{size}</span>
                            {gridSize === size && viewMode === 'grid' && <Check size={14} className="text-sky-600" />}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* CONTENT AREA */}
          <div className="bg-white/95 backdrop-blur rounded-2xl border border-slate-200 shadow-sm p-4 sm:p-6 min-h-[400px]">
            {fetchError ? (
              <div className="flex flex-col items-center justify-center h-64 text-rose-500 animate-in fade-in">
                <AlertCircle size={40} className="mb-2 opacity-50" />
                <p className="font-medium text-sm">{fetchError}</p>
                <button onClick={() => fetchRuns(user.id, 1, false)} className="mt-4 px-4 py-2 bg-rose-50 text-rose-700 rounded-lg text-xs font-bold hover:bg-rose-100">{t('history.tryAgain')}</button>
              </div>
            ) : historyTab === 'generate' ? (
              viewMode === 'list' ? (
                // LIST VIEW
                <div className="overflow-x-auto">
                  <table className="w-full text-xs text-left">
                    <thead className="text-slate-500 font-semibold border-b border-slate-100">
                      <tr>
                        <th className="py-3 px-2">{t('history.date')}</th>
                        <th className="py-3 px-2">{t('history.model')}</th>
                        <th className="py-3 px-2 text-right">{t('history.creditStart')}</th>
                        <th className="py-3 px-2 text-right">{t('history.creditLeft')}</th>
                        <th className="py-3 px-2 text-right">{t('history.usage')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                      {runs.map(run => (
                        <tr key={run.id} className="hover:bg-slate-50/50">
                          <td className="py-3 px-2 whitespace-nowrap text-slate-600">{new Date(run.created_at).toLocaleDateString()}</td>
                          <td className="py-3 px-2 font-medium text-slate-800">{run.model_name || "Unknown"}</td>
                          <td className="py-3 px-2 text-right text-slate-400">{run.credit_before ? parseInt(run.credit_before).toLocaleString('id-ID') : '-'}</td>
                          <td className="py-3 px-2 text-right text-slate-400">{run.credit_after ? parseInt(run.credit_after).toLocaleString('id-ID') : '-'}</td>
                          <td className={`py-3 px-2 text-right font-bold ${run.credits_charged === 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                            {run.credits_charged === 0 ? '0' : `-${parseInt(run.credits_charged).toLocaleString('id-ID')}`}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {!runsLoading && runs.length === 0 && <p className="text-center py-10 text-slate-400">{t('history.noHistory')}</p>}

                  {/* PAGINATION FOR LIST VIEW */}
                  {runs.length > 0 && (
                    <>
                      <div className="flex items-center justify-between mt-4 border-t border-slate-100 pt-3">
                        <button
                          disabled={currentPage === 1 || runsLoading}
                          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                          className="px-3 py-1.5 text-xs font-medium rounded-md border text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {t('history.previous')}
                        </button>
                        <div className="text-xs text-slate-400">
                          {t('history.pageInfo', { current: currentPage, total: totalPages })}
                        </div>
                        <button
                          disabled={!hasMore || runsLoading}
                          onClick={() => setCurrentPage(p => p + 1)}
                          className="px-3 py-1.5 text-xs font-medium rounded-md border text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {t('history.next')}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                // GRID VIEW
                <>
                  {!runsLoading && runs.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-20 text-slate-400 animate-in fade-in">
                      <List size={48} className="mb-4 opacity-20" />
                      <p className="text-sm font-medium">{t('history.noHistory')}</p>
                    </div>
                  ) : (
                    <div className={`grid gap-3 ${GRID_SIZES[gridSize] || GRID_SIZES.small}`}>
                      {runs.filter(r => r.result?.public_url).map(run => (
                        <div
                          key={run.id}
                          onClick={() => setMediaModal(run.result)}
                          className="group relative aspect-square bg-slate-50 rounded-xl border border-slate-200 overflow-hidden cursor-pointer hover:shadow-lg hover:ring-2 hover:ring-sky-400 transition-all"
                        >
                          <UnifiedPreview
                            result={run.result}
                            mimeType={run.result.mime_type}
                            isHistory={true}
                          />

                          {/* ACTION BUTTONS (Top Right - Smaller) */}
                          <div className="absolute top-2 right-2 flex gap-1.5 z-20 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                            {/* Recreate */}
                            <button
                              onClick={(e) => handleRecreate(e, run)}
                              className="w-6 h-6 flex items-center justify-center bg-black/40 hover:bg-sky-500 hover:text-white text-white rounded-full backdrop-blur-md transition-all shadow-sm"
                              title="Recreate"
                            >
                              <RotateCcw size={12} />
                            </button>

                            {/* Edit (Images only) */}
                            {run.result?.mime_type?.startsWith('image/') && (
                              <button
                                onClick={(e) => handleEdit(e, run)}
                                className="w-6 h-6 flex items-center justify-center bg-black/40 hover:bg-indigo-500 hover:text-white text-white rounded-full backdrop-blur-md transition-all shadow-sm"
                                title="Edit"
                              >
                                <Edit3 size={12} />
                              </button>
                            )}

                            {/* Delete */}
                            <button
                              onClick={(e) => handleDeleteItem(e, run)}
                              className="w-6 h-6 flex items-center justify-center bg-black/40 hover:bg-rose-500 hover:text-white text-white rounded-full backdrop-blur-md transition-all shadow-sm"
                              title="Delete"
                            >
                              <Trash2 size={12} />
                            </button>
                          </div>

                          {/* Overlay Info (Bottom) */}
                          <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/90 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300 flex flex-col justify-end">
                            <p className="text-white text-[10px] font-bold truncate">{run.model_name}</p>
                            <p className="text-white/70 text-[9px]">{new Date(run.created_at).toLocaleDateString()}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Modals for Edit/Delete */}
                  <DeleteItemModal
                    isOpen={!!assetToDelete}
                    onClose={() => setAssetToDelete(null)}
                    onConfirm={confirmDeleteAsset}
                    title="Hapus Item?"
                    message="Apakah anda takin ingin menghapus item ini? (Data transaksi tetap ada)."
                  />

                  {imageEditorAsset && (
                    <ImageEditorModal
                      isOpen={!!imageEditorAsset}
                      imageSrc={imageEditorAsset.url}
                      onClose={() => setImageEditorAsset(null)}
                      onSave={handleSaveEditedImage}
                      isSaving={isSavingEditedImage}
                    />
                  )}

                  {/* Loader */}
                  {hasMore && (
                    <div ref={observerTarget} className="py-8 flex justify-center w-full">
                      {runsLoading ? <Loader2 className="animate-spin text-slate-300" /> : <div className="h-4" />}
                    </div>
                  )}
                  {!hasMore && runs.length > 0 && (
                    <div className="py-8 text-center text-slate-300 text-[10px] uppercase tracking-widest font-semibold">{t('history.endOfHistory')}</div>
                  )}
                </>
              )
            ) : (
              // TOPUP HISTORY
              <div className="overflow-x-auto">
                <table className="w-full text-xs text-left">
                  <thead className="text-slate-500 font-semibold border-b border-slate-100">
                    <tr>
                      <th className="py-3 px-2">{t('history.date')}</th>
                      <th className="py-3 px-2">{t('history.txId')}</th>
                      <th className="py-3 px-2 text-right">{t('history.amountPaid')}</th>
                      <th className="py-3 px-2 text-right">{t('history.creditReceived')}</th>
                      <th className="py-3 px-2 text-right">{t('history.creditStart')}</th>
                      <th className="py-3 px-2 text-right">{t('history.creditEnd')}</th>
                      <th className="py-3 px-2 text-right">{t('history.status')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {transactions.map(tx => (
                      <tr key={tx.id} className="hover:bg-slate-50/50">
                        <td className="py-3 px-2 text-slate-600">{new Date(tx.paid_at).toLocaleDateString()}</td>
                        <td className="py-3 px-2 font-mono text-slate-500 text-[10px]">{tx.duitku_reference}</td>
                        <td className="py-3 px-2 text-right font-medium">Rp {parseInt(tx.amount_paid).toLocaleString('id-ID')}</td>
                        <td className="py-3 px-2 text-right font-bold text-emerald-600">+{parseInt(tx.credit_received || 0).toLocaleString('id-ID')}</td>
                        <td className="py-3 px-2 text-right text-slate-400">{tx.credit_before ? parseInt(tx.credit_before).toLocaleString('id-ID') : '-'}</td>
                        <td className="py-3 px-2 text-right text-slate-400">{tx.credit_after ? parseInt(tx.credit_after).toLocaleString('id-ID') : '-'}</td>
                        <td className="py-3 px-2 text-right">
                          <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-bold text-[10px] uppercase">
                            {tx.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* PAGINATION FOR TRANSACTIONS */}
                {transactions.length > 0 && (
                  <>
                    <div className="flex items-center justify-between mt-4 border-t border-slate-100 pt-3">
                      <button
                        disabled={currentTxPage === 1}
                        onClick={() => setCurrentTxPage(p => Math.max(1, p - 1))}
                        className="px-3 py-1.5 text-xs font-medium rounded-md border text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {t('history.previous')}
                      </button>
                      <div className="text-xs text-slate-400">
                        {t('history.pageInfo', { current: currentTxPage, total: totalTxPages })}
                      </div>
                      <button
                        disabled={currentTxPage >= totalTxPages}
                        onClick={() => setCurrentTxPage(p => p + 1)}
                        className="px-3 py-1.5 text-xs font-medium rounded-md border text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {t('history.next')}
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* FULL SCREEN MEDIA MODAL */}
      {mediaModal && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-md animate-in fade-in duration-200"
          onClick={() => setMediaModal(null)}
        >
          {(() => {
            // Determine Layout Mode based on content type
            const isDoc = (mediaModal.mime_type && (
              mediaModal.mime_type === 'application/pdf' ||
              mediaModal.mime_type === 'text/plain' ||
              mediaModal.mime_type.includes('application/') // For other docs
            )) || (mediaModal.public_url && (
              mediaModal.public_url.endsWith('.pdf') ||
              mediaModal.public_url.endsWith('.txt') ||
              mediaModal.public_url.endsWith('.doc') ||
              mediaModal.public_url.endsWith('.docx')
            ));

            return (
              <div
                className={`relative w-full h-full flex flex-col items-center justify-center pointer-events-none ${isDoc ? 'p-4 md:p-8 max-w-5xl gap-2' : ''}`}
              >
                {/* Header/Controls */}
                {isDoc ? (
                  // Document Mode: Header Bar (No Overlap)
                  <div className="w-full flex justify-end gap-3 pointer-events-auto bg-black/50 p-2 rounded-xl backdrop-blur-md">
                    {mediaModal.public_url && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const name = getCleanFilename(mediaModal.public_url);
                          handleDownload(mediaModal.public_url, name);
                        }}
                        className="flex items-center gap-2 px-4 py-2 bg-sky-600 hover:bg-sky-500 text-white rounded-lg text-xs font-bold transition-all shadow-sm"
                        title="Download"
                      >
                        <Download size={14} /> {t('history.downloadFile')}
                      </button>
                    )}
                    <button
                      onClick={() => setMediaModal(null)}
                      className="p-2 bg-white/10 hover:bg-white/20 text-white rounded-lg hover:scale-105 transition-all text-xs font-bold"
                    >
                      <X size={18} />
                    </button>
                  </div>
                ) : (
                  // Media Mode: Floating Buttons (Immersive)
                  <div className="absolute top-4 right-4 z-[110] flex items-center gap-3 pointer-events-auto">
                    {mediaModal.public_url && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const name = getCleanFilename(mediaModal.public_url);
                          handleDownload(mediaModal.public_url, name);
                        }}
                        className="p-3 bg-white/10 hover:bg-white/20 text-white rounded-full hover:scale-105 transition-all backdrop-blur-md border border-white/10 shadow-sm"
                        title="Download"
                      >
                        <Download size={20} />
                      </button>
                    )}
                    <button
                      onClick={() => setMediaModal(null)}
                      className="p-3 bg-white/10 hover:bg-white/20 text-white rounded-full hover:scale-105 transition-all backdrop-blur-md border border-white/10 shadow-sm"
                    >
                      <X size={20} />
                    </button>
                  </div>
                )}

                {/* Content Container */}
                <div
                  className={`w-full ${isDoc ? 'flex-1 min-h-0' : 'h-full'} flex items-center justify-center overflow-hidden pointer-events-auto`}
                  onClick={e => e.stopPropagation()}
                >
                  <UnifiedPreview result={mediaModal} mimeType={mediaModal.mime_type} isHistory={false} />
                </div>
              </div>
            );
          })()}
        </div>
      )}
    </>
  );
};

export default RiwayatGeneratePage;
